import {
  getScheduledPayments,
  getScheduledPaymentSettings,
  deleteScheduledPayment,
  postScheduledPayment,
  approveScheduledPayment,
  getScheduledPaymentList,
} from '../../api/api'
import { call, select, takeLatest, put, all } from 'redux-saga/effects'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { requestAlerts } from 'portal-modules/alerts-panel/redux/modules/alerts'
import createRequestSaga, {
  handleResponseError,
} from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { createSelector } from 'reselect'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'
import { requestPaymentMethods } from 'portal-modules/payments/redux/modules/paymentMethods'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { requestAutoPaymentStatus } from 'portal-modules/payments/redux/modules/autoPaymentStatus'

// action types
const REQUEST_AUTOPAYMENTS = 'rp/payment/REQUEST_AUTOPAYMENTS'
const RECEIVE_AUTOPAYMENTS = 'rp/payment/RECEIVE_AUTOPAYMENTS'
const DELETE_AUTOPAYMENT = 'rp/payment/DELETE_AUTOPAYMENT'
const AUTOPAYMENT_DELETED = 'rp/payment/AUTOPAYMENT_DELETED'
const REQUEST_SCHEDULED_PAYMENT_SETTINGS =
  'rp/payment/REQUEST_SCHEDULED_PAYMENT_SETTINGS'
const SAVE_AUTO_PAYMENT = 'rp/payment/SAVE_AUTO_PAYMENT'
const AUTO_PAYMENT_SAVED = 'rp/payment/AUTO_PAYMENT_SAVED'
const APPROVE_AUTO_PAYMENT = 'rp/payment/APPROVE_AUTO_PAYMENT'
const AUTO_PAYMENT_APPROVED = 'rp/payment/AUTO_PAYMENT_APPROVED'
const REQUEST_AUTOPAYMENT_LIST = 'rp/payment/REQUEST_AUTOPAYMENT_LIST'
const RECEIVE_AUTOPAYMENT_LIST = 'rp/payment/RECEIVE_AUTOPAYMENT_LIST'

// reducer
export default function autoPayments(state = {}, { type, payload }) {
  switch (type) {
    case RECEIVE_AUTOPAYMENTS:
      return { ...payload }
    case RECEIVE_AUTOPAYMENT_LIST:
      return { ...payload }
    case AUTOPAYMENT_DELETED:
      return Object.keys(state).reduce((obj, key) => {
        if (key == payload) return obj
        return { ...obj, [key]: state[key] }
      }, {})
    case AUTO_PAYMENT_SAVED:
    case AUTO_PAYMENT_APPROVED:
      return {
        ...state,
      }
    default:
      return state
  }
}

// action creators
export const requestAutoPayments = () => ({
  type: REQUEST_AUTOPAYMENTS,
})

export const requestAutoPaymentList = () => ({
  type: REQUEST_AUTOPAYMENT_LIST,
})

export const postAutoPaymentDeleted = payload => ({
  type: AUTOPAYMENT_DELETED,
  payload,
})

export const requestScheduledPaymentSettings = () => ({
  type: REQUEST_SCHEDULED_PAYMENT_SETTINGS,
})

export const deleteAutoPayment = (payload, callback) => ({
  type: DELETE_AUTOPAYMENT,
  callback,
  payload,
})

export const saveAutoPayment = createFormAction(SAVE_AUTO_PAYMENT)

export const approveAutoPayment = createFormAction(APPROVE_AUTO_PAYMENT)

// sagas
const getAllowedDays = str => str.split(',').map(d => +d)

const getSettings = ({
  allowed_days,
  bimonthly,
  end_date_options,
  ...rest
}) => ({
  ...rest,
  allowed_days: getAllowedDays(allowed_days),
  bimonthly: {
    ...bimonthly,
    allowed_days: getAllowedDays(bimonthly.allowed_days),
  },
  end_date_options,
})

function* fetchAutoPayments() {
  const { list, settings } = yield call(getScheduledPayments)
  const autoPayments = Object.entries(list).reduce((obj, [key, value]) => {
    if (!Array.isArray(value)) return obj
    return {
      ...obj,
      ...value.reduce(
        (valObj, payment) => ({
          ...valObj,
          [payment.id ? payment.id : payment.first_payment.id]: {
            ...payment,
            type: key,
            id: payment.id ? payment.id : payment.first_payment.id,
          },
        }),
        {},
      ),
    }
  }, {})

  if (Object.keys(autoPayments).length) {
    yield put({ type: RECEIVE_AUTOPAYMENTS, payload: autoPayments })
    yield put(requestAutoPaymentStatus())
  }

  if (Object.keys(settings).length) {
    yield put(addSettings({ scheduledPayments: getSettings(settings) }))
    yield put(addCustomContent({ scheduledPayments: settings.custom_text }))
  }
}

function* fetchAutoPaymentList() {
  const autoPaymentList = yield call(getScheduledPaymentList)
  const autoPayments = Object.entries(autoPaymentList).reduce(
    (obj, [key, value]) => {
      if (!Array.isArray(value)) return obj
      return {
        ...obj,
        ...value.reduce(
          (valObj, payment) => ({
            ...valObj,
            [payment.id ? payment.id : payment.first_payment.id]: {
              ...payment,
              type: key,
              id: payment.id ? payment.id : payment.first_payment.id,
            },
          }),
          {},
        ),
      }
    },
    {},
  )

  if (Object.keys(autoPayments).length) {
    yield put({ type: RECEIVE_AUTOPAYMENT_LIST, payload: autoPayments })
  }
}

function* fetchScheduledPaymentSettings() {
  const settings = yield call(getScheduledPaymentSettings)
  yield put(addSettings({ scheduledPayments: getSettings(settings) }))
}

function* postDeleteAutoPayment({ payload }) {
  yield call(deleteScheduledPayment, { scheduled_payment_id: payload })
  yield put({ type: AUTOPAYMENT_DELETED, payload })
  yield put(requestAutoPaymentStatus())
}

function* handleAutoPaymentError(err) {
  // TODO: Currently merging multiple field message and creating customized error message
  // however we will tweak it after discussing with product
  if (err?.fields?.length) {
    const { fields } = err
    let customizedErrorMessage = fields?.map(field => field?.message)?.join(' ')
    err = { code: err.code, message: customizedErrorMessage }
  }
  if (err.code == 90033) {
    yield put(addError({ key: 'autoPayments', message: err }))
    return
  }
  if ([1002, 3133, 3134].includes(err.code)) {
    const {
      settings: { payments },
    } = yield select()

    yield put(
      addSettings({
        payments: { ...payments, is_duplicate_payment: true },
      }),
    )
  }

  yield call(handleResponseError, err, { key: 'autoPayments' })
}

function* saveScheduledPayment({ payload, callback }) {
  const scheduledPayment = yield call(postScheduledPayment, payload)
  if (scheduledPayment) {
    const { autoPayments } = yield select()

    if (!autoPayments.length) {
      yield all([put(requestAutoPayments()), put(requestPaymentMethods())])
    } else {
      yield all([
        put({ type: AUTO_PAYMENT_SAVED, payload: scheduledPayment }),
        put(requestPaymentMethods()),
      ])
    }
  }

  if (typeof callback == 'function') {
    yield put(requestAlerts())
    callback()
  }
}

function* scheduledPaymentApproval({ payload }) {
  const scheduledPayment = yield call(approveScheduledPayment, payload)

  if (scheduledPayment) {
    yield put({ type: AUTO_PAYMENT_APPROVED })
  }
}

function* watchRequestScheduledPaymentSettings() {
  yield takeLatest(
    REQUEST_SCHEDULED_PAYMENT_SETTINGS,
    createRequestSaga(fetchScheduledPaymentSettings, {
      key: 'payments',
      messgae: __('Fetching scheduled payment settings'),
      duration: 500,
    }),
  )
}

function* watchRequestAutoPayments() {
  yield takeLatest(
    REQUEST_AUTOPAYMENTS,
    createRequestSaga(fetchAutoPayments, {
      key: 'payments',
      message: __('Checking for auto payments'),
    }),
  )
}

function* watchRequestAutoPaymentList() {
  yield takeLatest(
    REQUEST_AUTOPAYMENT_LIST,
    createRequestSaga(fetchAutoPaymentList, {
      key: 'payments',
      message: __('Checking for auto payments'),
    }),
  )
}

function* watchSaveAutoPayment() {
  yield takeLatest(
    SAVE_AUTO_PAYMENT,
    createRequestSaga(saveScheduledPayment, {
      key: 'autoPayments',
      message: __('Saving scheduled payment'),
      successMessage: __('Auto payment saved.'),
      duration: 500,
      write: true,
      onError: handleAutoPaymentError,
    }),
  )
}

function* watchDeleteAutoPayment() {
  yield takeLatest(
    DELETE_AUTOPAYMENT,
    createRequestSaga(postDeleteAutoPayment, {
      key: 'payments',
      message: __('Deleting auto payment'),
      successMessage: __('Auto payment deleted'),
      write: true,
    }),
  )
}

function* watchApproveAutoPayment() {
  yield takeLatest(
    APPROVE_AUTO_PAYMENT,
    createRequestSaga(scheduledPaymentApproval, {
      key: 'autoPayments',
      message: __('Saving auto payment'),
      successMessage: __('Auto payment saved.'),
      duration: 500,
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestAutoPayments,
  watchDeleteAutoPayment,
  watchRequestScheduledPaymentSettings,
  watchSaveAutoPayment,
  watchApproveAutoPayment,
  watchRequestAutoPaymentList,
]

// selectors
const chargesSelector = ({ balanceDue: { future = {} } }) => future.total || 0
const settingsSelector = ({ settings }) => {
  return {
    scheduledPayments: settings.scheduledPayments || {},
    paymentSettings: settings.payments || {},
  }
}

export const monthlySettingsSelector = createSelector(
  chargesSelector,
  settingsSelector,
  (
    charge,
    {
      scheduledPayments: {
        amount_types_allowed,
        allowed_days = [],
        end_date_options,
      },
      paymentSettings: { payment_account_required },
    },
  ) => {
    let allowFixed = amount_types_allowed && amount_types_allowed.fixed
    let allowVariable =
      amount_types_allowed &&
      amount_types_allowed.variable &&
      amount_types_allowed.variable.allowed
    let hideMaxAmount =
      amount_types_allowed &&
      amount_types_allowed.variable &&
      amount_types_allowed.variable.ceiling &&
      amount_types_allowed.variable.ceiling.hidden
    let requireMaxAmount =
      !hideMaxAmount &&
      (amount_types_allowed &&
        amount_types_allowed.variable &&
        amount_types_allowed.variable.ceiling &&
        amount_types_allowed.variable.ceiling.required)

    return {
      allowedDays: allowed_days,
      charge,
      endDateOptions: end_date_options,
      allowFixed,
      allowVariable,
      requireMaxAmount,
      showMaxAmount: !hideMaxAmount,
      paymentAccountRequired: payment_account_required,
    }
  },
)

export const disableAutoPaymentsSelector = createSelector(
  monthlySettingsSelector,
  ({ allowFixed, allowVariable, requireMaxAmount, showMaxAmount, charge }) => {
    let shouldDisablePayments = false

    if (charge === 0) {
      if (
        (!allowFixed && allowVariable && !requireMaxAmount && !showMaxAmount) ||
        (!allowFixed && !allowVariable)
      ) {
        shouldDisablePayments = true
      }
    }

    return { shouldDisablePayments }
  },
)

export const biMonthlySettingsSelector = createSelector(
  settingsSelector,
  monthlySettingsSelector,
  (
    {
      scheduledPayments: {
        bimonthly: { allowed_days = [] },
      },
    },
    monthly,
  ) => ({
    ...monthly,
    allowedDays: allowed_days,
  }),
)

export const roommateSplitSettingsSelector = createSelector(
  settingsSelector,
  monthlySettingsSelector,
  (
    { scheduledPayments: { roommates, end_date_options, allowed_days } },
    monthly,
  ) => ({
    ...monthly,
    end_date_options,
    allowed_days,
    roommates:
      roommates &&
      roommates.reduce(
        (acc, roommate) => [
          ...acc,
          ...[
            {
              id: roommate.customer_id,
              label: `${roommate.name_first} ${roommate.name_last}`,
            },
          ],
        ],
        [],
      ),
  }),
)
